import React, { useCallback, useEffect, useMemo, useState, type FunctionComponent } from "react";
import { DeleteOutlined, UserAddOutlined, WarningOutlined } from "@ant-design/icons";
import { Button, DatePicker, Empty, Form, Input, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";

import { EMAIL_REGEX } from "@fitness-app/utils/src/constants/emailRegex";

import {
  decodeAccessSource,
  encodeAccessSource,
  importRowKey as rowKey,
  type AccessSourceValue,
} from "../accessSource";
import { type ManualItemModel, type RowProblem, type TableModel } from "../ImportClientsModal";
import { useAccessSourceOptions } from "../useAccessSourceOptions";

interface OwnProps {
  productId: string;
  setAccessSourceException: (rowKey: string, source: AccessSourceValue) => void;
  setDateException: (rowKey: string, date: dayjs.Dayjs | null) => void;
  setDateSubscriberException: (id: string, date: dayjs.Dayjs | null) => void;
  setEmail: (id: string, value: string) => void;
  setName: (id: string, value: string) => void;
  setPhone: (id: string, value: string) => void;
  tableModel: TableModel[];
  setSelectedRows: (keys: string[]) => void;
  selectedClients: string[];
  addNewRow: () => void;
  removeItem: (id: string) => void;
  isFree: boolean;
  previewLoading: boolean;
}

type Props = OwnProps;

const EmailField = (row: ManualItemModel & { setEmail: OwnProps["setEmail"] }) => {
  const [inputValue, setValue] = useState(row.email);
  const [debouncedValue] = useDebounce(inputValue, 700);
  const [hasError, setState] = useState<boolean>(false);

  useEffect(() => {
    if (debouncedValue) {
      setState(!debouncedValue.match(EMAIL_REGEX));
    } else {
      setState(false);
    }
    row.setEmail(row.id, debouncedValue);
  }, [debouncedValue]);

  return (
    <Form.Item hasFeedback={hasError} style={{ marginBottom: -2 }} validateStatus={hasError ? "error" : undefined}>
      <Input value={inputValue} onChange={(e) => setValue(e.target.value?.trim())} />
    </Form.Item>
  );
};

const NameField = (row: ManualItemModel & { setName: OwnProps["setName"] }) => {
  const [inputValue, setValue] = useState(row.name);
  const [debouncedValue] = useDebounce(inputValue, 700);

  useEffect(() => {
    row.setName(row.id, debouncedValue);
  }, [debouncedValue]);

  return <Input value={inputValue} onChange={(e) => setValue(e.target.value)} />;
};

const SubscribeDateField = (
  row: ManualItemModel & {
    setDateSubscriberException: OwnProps["setDateSubscriberException"];
  },
) => {
  const getDisabledDate = useCallback((current: dayjs.Dayjs) => current && current > dayjs().endOf("day"), []);
  return (
    <DatePicker
      format="DD.MM.YYYY"
      disabledDate={getDisabledDate}
      value={row.subscribedDate || undefined}
      allowClear={false}
      onChange={(date) => row.setDateSubscriberException(row.id, date)}
    />
  );
};

const PROBLEM_KEY: Record<NonNullable<RowProblem>, string> = {
  wrongDate: "wrongDate",
  noSourceAccess: "noSourceAccess",
  invalidEmail: "invalidEmail",
};

const ParseClientsList: FunctionComponent<Props> = ({
  productId,
  setAccessSourceException,
  tableModel,
  setDateException,
  setSelectedRows,
  selectedClients,
  addNewRow,
  setEmail,
  setDateSubscriberException,
  setName,
  removeItem,
  isFree,
  setPhone,
  previewLoading,
}) => {
  const { t } = useTranslation("products");
  const accessSourceOptions = useAccessSourceOptions(productId);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedClients,
      onChange: (selected: React.Key[]) => setSelectedRows(selected as string[]),
      getCheckboxProps: (record: TableModel) => ({
        name: record.email,
        disabled: record.problem !== null,
      }),
    }),
    [setSelectedRows, selectedClients],
  );

  const getDisabledDate = useCallback((current: dayjs.Dayjs) => current && current < dayjs().endOf("day"), []);

  return (
    <div className="py-5">
      <Table<TableModel>
        size="small"
        dataSource={tableModel}
        pagination={false}
        loading={previewLoading}
        rowKey={rowKey}
        // Fixed column widths + horizontal scroll: with seven inputs in a row the
        // auto layout squeezed the phone and the access source until unreadable.
        scroll={{ x: 1360 }}
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}>
              <Button icon={<UserAddOutlined />} type="primary" onClick={addNewRow}>
                {t("import.addFirstClient")}
              </Button>
            </Empty>
          ),
        }}
      >
        <Table.Column<TableModel>
          title={t("productTransactions.tableHeader.index")}
          width={42}
          dataIndex="index"
          ellipsis
          key="index"
          render={(name, row, i) => i + 1}
        />
        <Table.Column<TableModel | ManualItemModel>
          title={t("productTransactions.tableHeader.name")}
          dataIndex="name"
          key="name"
          width={200}
          render={(name: string, row) =>
            row.id ? <NameField {...(row as ManualItemModel)} setName={setName} /> : name
          }
        />
        <Table.Column<TableModel | ManualItemModel>
          title={t("productTransactions.tableHeader.email")}
          dataIndex="email"
          key="email"
          width={260}
          render={(email: string, row) => (
            <Space direction="vertical" size={2}>
              {row.id ? <EmailField {...(row as ManualItemModel)} setEmail={setEmail} /> : email}
              {row.preview?.existingStatus === "active" && (
                <Tag color="gold" style={{ marginInlineEnd: 0 }}>
                  {t("import.preview.alreadyActive")}
                </Tag>
              )}
              {row.preview?.existingStatus === "archived" && <Tag>{t("import.preview.previouslyArchived")}</Tag>}
            </Space>
          )}
        />
        <Table.Column<TableModel | ManualItemModel>
          title={t("productTransactions.tableHeader.phoneNumber")}
          dataIndex="phoneNumber"
          key="phoneNumber"
          width={160}
          render={(email: string, row) =>
            row.id ? <NameField {...(row as ManualItemModel)} setName={setPhone} /> : email
          }
        />
        <Table.Column<TableModel | ManualItemModel>
          title={t("productClients.tableHeader.createdAt")}
          dataIndex="subscribedDate"
          key="subscribedDate"
          width={150}
          render={(subscribedDate: string, row) =>
            row.id ? (
              <SubscribeDateField
                {...(row as ManualItemModel)}
                setDateSubscriberException={setDateSubscriberException}
              />
            ) : (
              dayjs(subscribedDate).format("DD.MM.YYYY")
            )
          }
        />
        {!isFree && (
          <>
            <Table.Column<TableModel | ManualItemModel>
              title={t("import.accessSource.column")}
              dataIndex="accessSource"
              width={340}
              key="accessSource"
              render={(accessSource: AccessSourceValue | null, row) => (
                <Select
                  style={{ width: "100%" }}
                  showSearch
                  optionFilterProp="label"
                  placeholder={t("import.accessSource.placeholder")}
                  options={accessSourceOptions}
                  popupMatchSelectWidth={false}
                  value={accessSource ? encodeAccessSource(accessSource) : undefined}
                  onChange={(value: string) => {
                    const decoded = decodeAccessSource(value);

                    if (decoded) {
                      setAccessSourceException(rowKey(row), decoded);
                    }
                  }}
                />
              )}
            />
            <Table.Column<TableModel>
              title={t("import.headerForm.accessTo")}
              dataIndex="accessTo"
              key="accessTo"
              width={300}
              render={(accessTo: Dayjs | null, row) => (
                <div className="flex items-center">
                  {row.accessSource?.kind === "linked" ? (
                    <Typography.Text>
                      {accessTo ? accessTo.format("DD.MM.YYYY") : t("import.accessSource.unlimited")}
                    </Typography.Text>
                  ) : (
                    <DatePicker
                      format="DD.MM.YYYY HH:mm"
                      disabledDate={getDisabledDate}
                      value={accessTo || undefined}
                      placeholder={t<string>("import.accessSource.unlimited")}
                      allowClear={row.accessSource?.kind === "manual"}
                      onChange={(date) => setDateException(rowKey(row), date)}
                    />
                  )}
                  {row.problem && row.problem !== "invalidEmail" && (
                    <Tooltip title={t(`import.problem.${PROBLEM_KEY[row.problem]}Tooltip`)}>
                      <div>
                        <WarningOutlined className="my-0 ml-1 mr-2 text-lg text-red-600" />
                        <Typography.Text type="danger">
                          {t(`import.problem.${PROBLEM_KEY[row.problem]}`)}
                        </Typography.Text>
                      </div>
                    </Tooltip>
                  )}
                </div>
              )}
            />
          </>
        )}

        <Table.Column<TableModel | ManualItemModel>
          dataIndex="action"
          key="action"
          width={48}
          fixed="right"
          render={(_, { id }) =>
            id ? (
              <DeleteOutlined
                style={{ color: "#f64e60", fontSize: 16, cursor: "pointer" }}
                onClick={() => removeItem(id)}
              />
            ) : null
          }
        />
      </Table>

      <Button icon={<UserAddOutlined />} type="primary" onClick={addNewRow} style={{ marginTop: 30 }}>
        {t("import.addClientManually")}
      </Button>
    </div>
  );
};

export default ParseClientsList;
